import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Button, Alert } from './ui';
import { 
  getThemes,
  getAutoAssignmentConfiguration,
  setAutoAssignmentConfiguration,
  runAutoAssignment,
  getAutoAssignmentPreview,
  getAutoAssignmentStatus,
  getGroupFilterOptions
} from '../services/projectService';
import {
  PlayIcon,
  AdjustmentsHorizontalIcon,
  ChartBarIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';

const EMPTY_FILTERS = {
  countries: [],
  languages: [],
  minMembers: '',
  maxMembers: '',
  qualityScore: ''
};

const ACTIVE_PROCESS_STATUSES = new Set(['pending', 'in_progress', 'paused']);

const parseConfigurationFilters = (config) => {
  const parsedFilters = { ...EMPTY_FILTERS };
  const configFilters = Array.isArray(config?.filters) ? config.filters : [];

  configFilters.forEach((filter) => {
    switch (filter.filter_type) {
      case 'country':
        parsedFilters.countries.push(filter.filter_value);
        break;
      case 'language':
        parsedFilters.languages.push(filter.filter_value);
        break;
      case 'min_members':
        parsedFilters.minMembers = filter.filter_value;
        break;
      case 'max_members':
        parsedFilters.maxMembers = filter.filter_value;
        break;
      case 'quality_score':
        parsedFilters.qualityScore = filter.filter_value;
        break;
      default:
        break;
    }
  });

  return parsedFilters;
};

const buildProcessSummary = (process) => {
  const assignment = process?.process_data?.assignment || null;
  const reconciliation = process?.process_data?.reconciliation || null;
  if (!assignment && !reconciliation) {
    return null;
  }

  return {
    processed: assignment?.candidatesChecked || process?.processed_items || process?.progress?.processed || 0,
    assigned: assignment?.assignmentsSuccess || process?.successful_items || process?.progress?.successful || 0,
    skipped: assignment?.skippedCount || process?.skipped_items || 0,
    failed: assignment?.failedCount || process?.failed_items || process?.progress?.failed || 0,
    removedCount: reconciliation?.removedCount || 0,
    checkedChats: reconciliation?.checkedChats || 0,
  };
};

const ProjectAutoAssignmentEnhanced = ({ projectId }) => {
  const [themes, setThemes] = useState([]);
  const [selectedThemes, setSelectedThemes] = useState([]);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [preview, setPreview] = useState(null);
  const [allThemesStats, setAllThemesStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [themesError, setThemesError] = useState(null);
  const [configurationError, setConfigurationError] = useState(null);
  const [previewError, setPreviewError] = useState(null);
  const [assignmentProcess, setAssignmentProcess] = useState(null);
  const [assignmentResult, setAssignmentResult] = useState(null);
  const [availableCountries, setAvailableCountries] = useState([]);
  const lastProcessStatusRef = useRef(null);
  const lastLoadedProjectIdRef = useRef(null);

  const availableLanguages = ['mix', 'unknown', 'ru', 'en', 'uz', 'uk', 'id', 'ar', 'th', 'tr', 'be', 'kk', 'de', 'fr', 'it', 'es', 'pt', 'zh'];

  const isAssignmentProcessActive = ACTIVE_PROCESS_STATUSES.has(assignmentProcess?.status);

  const fetchData = useCallback(async () => {
    if (!projectId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setThemesError(null);
      setConfigurationError(null);
      setPreviewError(null);

      const [themesResult, configResult, statusResult, countryOptionsResult] = await Promise.allSettled([
        getThemes(),
        getAutoAssignmentConfiguration(projectId),
        getAutoAssignmentStatus(projectId),
        getGroupFilterOptions(),
      ]);

      let nextThemes = [];
      let nextSelectedThemes = [];
      let nextFilters = { ...EMPTY_FILTERS };

      if (themesResult.status === 'fulfilled') {
        nextThemes = Array.isArray(themesResult.value) ? themesResult.value : [];
        setThemes(nextThemes);
      } else {
        console.error('Error fetching themes:', themesResult.reason);
        setThemes([]);
        setThemesError('Failed to load themes. Retry when the backend is stable.');
      }

      if (configResult.status === 'fulfilled') {
        const config = configResult.value || {};
        nextSelectedThemes = Array.isArray(config.themes) ? config.themes.map((theme) => theme.id) : [];
        nextFilters = parseConfigurationFilters(config);
        setSelectedThemes(nextSelectedThemes);
        setFilters(nextFilters);
      } else {
        console.error('Error fetching auto-assignment configuration:', configResult.reason);
        setSelectedThemes([]);
        setFilters({ ...EMPTY_FILTERS });
        setConfigurationError('Failed to load saved auto-assignment configuration. You can still edit and save new country-only settings.');
      }

      if (statusResult.status === 'fulfilled') {
        const currentProcess = statusResult.value?.process || null;
        setAssignmentProcess(currentProcess);
        lastProcessStatusRef.current = currentProcess?.status || null;
        const nextSummary = buildProcessSummary(currentProcess);
        if (nextSummary) {
          setAssignmentResult(nextSummary);
        } else {
          setAssignmentResult(null);
        }
      } else {
        console.warn('Error fetching auto-assignment status:', statusResult.reason);
      }

      if (countryOptionsResult.status === 'fulfilled') {
        setAvailableCountries(Array.isArray(countryOptionsResult.value?.countries) ? countryOptionsResult.value.countries : []);
      } else {
        console.warn('Error fetching country filter options:', countryOptionsResult.reason);
        setAvailableCountries([]);
      }
    } catch (err) {
      console.error('Error fetching auto-assignment data:', err);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  const buildFiltersArray = useCallback((sourceFilters = filters) => {
    const filtersArray = [];

    sourceFilters.countries.forEach(country => {
      filtersArray.push({ type: 'country', value: country });
    });

    sourceFilters.languages.forEach(language => {
      filtersArray.push({ type: 'language', value: language });
    });

    if (sourceFilters.minMembers) {
      filtersArray.push({ type: 'min_members', value: parseInt(sourceFilters.minMembers) });
    }

    if (sourceFilters.maxMembers) {
      filtersArray.push({ type: 'max_members', value: parseInt(sourceFilters.maxMembers) });
    }

    if (sourceFilters.qualityScore) {
      filtersArray.push({ type: 'quality_score', value: parseFloat(sourceFilters.qualityScore) });
    }

    return filtersArray;
  }, [filters]);

  const fetchAllThemesStats = useCallback(async (themesData = null, customFilters = null) => {
    try {
      // Fetch stats for ALL themes to show initial counts with current filters
      const themesToUse = themesData || themes;
      if (themesToUse.length > 0) {
        const allThemeIds = themesToUse.map(theme => theme.id);
        
        // Use custom filters if provided, otherwise build from current state
        const filtersArray = customFilters || buildFiltersArray();
        
        const allThemesData = await getAutoAssignmentPreview(projectId, allThemeIds, filtersArray);
        setAllThemesStats(allThemesData);
      }
    } catch (err) {
      console.warn('Error fetching all themes stats:', err);
    }
  }, [buildFiltersArray, projectId, themes]);

  const fetchPreview = useCallback(async (customThemes = selectedThemes, customFilters = filters) => {
    try {
      setPreviewLoading(true);
      setPreviewError(null);
      const filtersArray = buildFiltersArray(customFilters);
      const previewData = await getAutoAssignmentPreview(projectId, customThemes, filtersArray);
      setPreview(previewData);
    } catch (err) {
      console.error('Error fetching preview:', err);
      setPreviewError(err.response?.data?.message || 'Preview is temporarily unavailable');
    } finally {
      setPreviewLoading(false);
    }
  }, [buildFiltersArray, filters, projectId, selectedThemes]);

  const fetchAssignmentStatus = useCallback(async ({ silent = false } = {}) => {
    try {
      const statusResponse = await getAutoAssignmentStatus(projectId);
      const nextProcess = statusResponse?.process || null;
      const previousStatus = lastProcessStatusRef.current;

      setAssignmentProcess(nextProcess);

      const nextSummary = buildProcessSummary(nextProcess);
      if (nextSummary) {
        setAssignmentResult(nextSummary);
      } else {
        setAssignmentResult(null);
      }

      const nextStatus = nextProcess?.status || null;
      if (previousStatus !== nextStatus) {
        if (nextStatus === 'completed') {
          setSuccess('Auto-assignment completed successfully!');
          setError(null);
        } else if (nextStatus === 'failed') {
          setError(`Auto-assignment failed: ${nextProcess?.error_message || 'Unknown error'}`);
        }
      }

      lastProcessStatusRef.current = nextStatus;
    } catch (err) {
      if (!silent) {
        console.error('Error fetching auto-assignment status:', err);
      }
    }
  }, [projectId]);

  useEffect(() => {
    if (!projectId) {
      return undefined;
    }

    if (lastLoadedProjectIdRef.current === projectId) {
      return undefined;
    }

    lastLoadedProjectIdRef.current = projectId;
    void fetchData();

    return undefined;
  }, [fetchData, projectId]);

  useEffect(() => {
    if (loading) {
      return undefined;
    }

    const timeoutId = setTimeout(() => {
      fetchPreview();
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [fetchPreview, loading]);

  useEffect(() => {
    if (loading) {
      return undefined;
    }

    const timeoutId = setTimeout(() => {
      if (themes.length > 0) {
        fetchAllThemesStats();
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [fetchAllThemesStats, loading, themes]);

  useEffect(() => {
    if (!isAssignmentProcessActive) {
      return undefined;
    }

    const intervalId = setInterval(() => {
      fetchAssignmentStatus({ silent: true });
    }, 2000);

    return () => clearInterval(intervalId);
  }, [fetchAssignmentStatus, isAssignmentProcessActive]);

  const handleThemeToggle = (themeId) => {
    setSelectedThemes(prev => 
      prev.includes(themeId)
        ? prev.filter(id => id !== themeId)
        : [...prev, themeId]
    );
  };

  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  const handleCountryToggle = (country) => {
    setFilters(prev => ({
      ...prev,
      countries: prev.countries.includes(country)
        ? prev.countries.filter(c => c !== country)
        : [...prev.countries, country]
    }));
  };

  const handleLanguageToggle = (language) => {
    setFilters(prev => ({
      ...prev,
      languages: prev.languages.includes(language)
        ? prev.languages.filter(l => l !== language)
        : [...prev.languages, language]
    }));
  };

  const handleSelectAllCountries = () => {
    setFilters(prev => ({
      ...prev,
      countries: availableCountries.map((country) => country.value)
    }));
  };

  const handleClearAllCountries = () => {
    setFilters(prev => ({
      ...prev,
      countries: []
    }));
  };

  const handleSelectAllLanguages = () => {
    setFilters(prev => ({
      ...prev,
      languages: [...availableLanguages]
    }));
  };

  const handleClearAllLanguages = () => {
    setFilters(prev => ({
      ...prev,
      languages: []
    }));
  };

  const handleSelectAllThemes = () => {
    const allThemeIds = themes.map(theme => theme.id);
    setSelectedThemes(allThemeIds);
  };

  const handleClearAllThemes = () => {
    setSelectedThemes([]);
  };

  const getThemeGroupCount = (themeId) => {
    // First try to get count from current preview (for selected themes)
    if (preview && preview.themes) {
      const themeData = preview.themes.find(t => t.id === themeId);
      if (themeData) {
        return themeData.groupCount;
      }
    }
    
    // Fallback to allThemesStats to show counts for all themes at initial load
    if (allThemesStats && allThemesStats.themes) {
      const themeData = allThemesStats.themes.find(t => t.id === themeId);
      if (themeData) {
        return themeData.groupCount;
      }
    }
    
    return 0;
  };

  const handleSaveConfiguration = async () => {
    const filtersArray = buildFiltersArray();
    
    // Check if at least themes OR filters are selected
    if (selectedThemes.length === 0 && filtersArray.length === 0) {
      setError('Please select at least one theme OR set up filters (country, language, etc.)');
      return;
    }

    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      const configuration = {
        themes: selectedThemes,
        filters: filtersArray
      };

      const result = await setAutoAssignmentConfiguration(projectId, configuration);

      if (result.process) {
        setAssignmentProcess(result.process);
        lastProcessStatusRef.current = result.process.status || null;
        const nextSummary = buildProcessSummary(result.process);
        if (nextSummary) {
          setAssignmentResult(nextSummary);
        }
      }

      setSuccess(
        result.reusedExisting
          ? 'Configuration saved. Existing auto-assignment process is still running.'
          : 'Configuration saved. Auto-assignment started.'
      );
    } catch (err) {
      console.error('Error saving configuration:', err);
      setError('Failed to save configuration: ' + (err.response?.data?.message || err.message));
    } finally {
      setSaving(false);
    }
  };

  const handleRunAutoAssignment = async () => {
    try {
      setRunning(true);
      setError(null);
      setSuccess(null);

      const result = await runAutoAssignment(projectId);

      if (result.process) {
        setAssignmentProcess(result.process);
        lastProcessStatusRef.current = result.process.status || null;
        const nextSummary = buildProcessSummary(result.process);
        if (nextSummary) {
          setAssignmentResult(nextSummary);
        }
      }

      setSuccess(
        result.reusedExisting
          ? 'Auto-assignment is already running for this project.'
          : 'Auto-assignment started.'
      );
    } catch (err) {
      console.error('Error running auto-assignment:', err);
      setError('Failed to run auto-assignment: ' + (err.response?.data?.message || err.message));
    } finally {
      setRunning(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {error && (
        <Alert variant="error" className="mb-4">
          {error}
        </Alert>
      )}

      {themesError && (
        <Alert variant="error" className="mb-4">
          <div className="flex items-center justify-between gap-4">
            <span>{themesError}</span>
            <Button size="sm" variant="secondary" onClick={fetchData}>
              Retry
            </Button>
          </div>
        </Alert>
      )}

      {configurationError && (
        <Alert variant="warning" className="mb-4">
          <div className="flex items-center justify-between gap-4">
            <span>{configurationError}</span>
            <Button size="sm" variant="secondary" onClick={fetchData}>
              Retry
            </Button>
          </div>
        </Alert>
      )}

      {success && (
        <Alert variant="success" className="mb-4">
          {success}
        </Alert>
      )}

      {previewError && (
        <Alert variant="warning" className="mb-4">
          Preview is temporarily unavailable. You can still edit and save country-only settings.
        </Alert>
      )}

      {assignmentProcess && (
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
          <div className="flex items-center justify-between gap-4">
            <div>
              <h4 className="text-sm font-medium text-gray-900">Auto-assignment process</h4>
              <p className="mt-1 text-sm text-gray-600">
                Status: <span className="font-medium">{assignmentProcess.status}</span>
                {assignmentProcess.started_at && (
                  <span className="ml-2 text-xs text-gray-500">
                    started {new Date(assignmentProcess.started_at).toLocaleString()}
                  </span>
                )}
              </p>
              {assignmentProcess.error_message && (
                <p className="mt-1 text-sm text-red-600">{assignmentProcess.error_message}</p>
              )}
            </div>
            <Button size="sm" variant="secondary" onClick={() => fetchAssignmentStatus()}>
              Refresh status
            </Button>
          </div>
          {assignmentProcess.progress && (
            <div className="mt-3 text-sm text-gray-700">
              Progress: {assignmentProcess.progress.processed} / {assignmentProcess.progress.total || 0}
              {assignmentResult && (
                <span className="ml-2 text-xs text-gray-500">
                  assigned {assignmentResult.assigned}, skipped {assignmentResult.skipped}, failed {assignmentResult.failed}
                </span>
              )}
            </div>
          )}
        </div>
      )}

      {/* Assignment Preview */}
      {preview && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center mb-2">
            <ChartBarIcon className="h-5 w-5 text-blue-600 mr-2" />
            <h4 className="text-sm font-medium text-blue-900">Assignment Preview</h4>
            {previewLoading && (
              <div className="ml-2 animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
            )}
          </div>
          <div className="text-sm text-blue-800">
            <p className="font-medium">📊 Total matching groups: {preview.totalGroups.toLocaleString()}</p>
            {preview.availableForAssignment !== undefined && (
              <p className="font-medium">
                🎯 Available for assignment: {preview.availableForAssignment.toLocaleString()}
                {preview.availableForAssignment !== preview.totalGroups && (
                  <span className="text-xs font-normal ml-1">
                    ({(preview.totalGroups - preview.availableForAssignment).toLocaleString()} already assigned)
                  </span>
                )}
              </p>
            )}
            {preview.themes && preview.themes.length > 0 && (
              <div className="mt-2">
                <p className="text-xs font-medium mb-1">Breakdown by theme:</p>
                <div className="grid grid-cols-1 gap-1 text-xs">
                  {preview.themes.map(theme => (
                    <div key={theme.id} className="flex justify-between">
                      <span>• {theme.name}:</span>
                      <span className="font-medium">{theme.groupCount.toLocaleString()} groups</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {preview.note && (
              <div className="mt-2 p-2 bg-yellow-100 border border-yellow-300 rounded text-xs text-yellow-800">
                ⚠️ {preview.note}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Theme Selection */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900">Select Themes</h3>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={handleSelectAllThemes}
              className="text-xs text-primary-600 hover:text-primary-700 underline"
            >
              Выбрать все
            </button>
            <button
              type="button"
              onClick={handleClearAllThemes}
              className="text-xs text-gray-500 hover:text-gray-700 underline"
            >
              Очистить все
            </button>
          </div>
        </div>
        <p className="text-sm text-gray-500 mb-4">
          Choose themes that match the content you want to automatically assign to this project.
        </p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {Array.isArray(themes) && themes.length > 0 ? themes.map(theme => {
            const groupCount = getThemeGroupCount(theme.id);
            const isSelected = selectedThemes.includes(theme.id);
            
            return (
              <div key={theme.id} className="relative">
                <label className={`flex items-center p-3 border rounded-lg cursor-pointer transition-colors ${
                  isSelected ? 'border-primary-500 bg-primary-50' : 'border-gray-200 hover:bg-gray-50'
                }`}>
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => handleThemeToggle(theme.id)}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <div className="ml-3 flex-1">
                    <div className="flex items-center justify-between">
                      <div className="text-sm font-medium text-gray-900">
                        {theme.name}
                      </div>
                      <div className={`text-xs font-medium px-2 py-1 rounded ${
                        isSelected ? 'text-primary-600 bg-primary-100' : 'text-gray-600 bg-gray-100'
                      }`}>
                        {previewLoading ? '...' : `${groupCount.toLocaleString()} groups`}
                      </div>
                    </div>
                    {theme.description && (
                      <div className="text-xs text-gray-500 mt-1">
                        {theme.description}
                      </div>
                    )}
                  </div>
                </label>
              </div>
            );
          }) : themesError ? (
            <div className="col-span-full text-center py-4 text-red-600">
              Themes failed to load. Retry after the backend stabilizes.
            </div>
          ) : (
            <div className="col-span-full text-center py-4 text-gray-500">
              No themes available. Please add themes first.
            </div>
          )}
        </div>
      </div>

      {/* Filters */}
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">Additional Filters</h3>
        <p className="text-sm text-gray-500 mb-4">
          Set additional criteria to refine which groups get automatically assigned.
        </p>
        <div className="mb-4 rounded-md border border-blue-200 bg-blue-50 p-3 text-xs text-blue-900">
          Geographic project scope currently uses <span className="font-semibold">country only</span>. Region and city remain group metadata and are not used for project assignment yet.
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Countries */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">
                Countries
              </label>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={handleSelectAllCountries}
                  className="text-xs text-primary-600 hover:text-primary-700 underline"
                >
                  Выбрать все
                </button>
                <button
                  type="button"
                  onClick={handleClearAllCountries}
                  className="text-xs text-gray-500 hover:text-gray-700 underline"
                >
                  Очистить все
                </button>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {availableCountries.map(country => (
                <label key={country.value} className="flex items-center">
                  <input
                    type="checkbox"
                    checked={filters.countries.includes(country.value)}
                    onChange={() => handleCountryToggle(country.value)}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm text-gray-700">{country.label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Languages */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">
                Languages
              </label>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={handleSelectAllLanguages}
                  className="text-xs text-primary-600 hover:text-primary-700 underline"
                >
                  Выбрать все
                </button>
                <button
                  type="button"
                  onClick={handleClearAllLanguages}
                  className="text-xs text-gray-500 hover:text-gray-700 underline"
                >
                  Очистить все
                </button>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {availableLanguages.map(language => (
                <label key={language} className="flex items-center">
                  <input
                    type="checkbox"
                    checked={filters.languages.includes(language)}
                    onChange={() => handleLanguageToggle(language)}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm text-gray-700">{language}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Member Count */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Member Count Range
            </label>
            <div className="grid grid-cols-2 gap-2">
              <input
                type="number"
                placeholder="Min members"
                value={filters.minMembers}
                onChange={(e) => handleFilterChange('minMembers', e.target.value)}
                className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
              />
              <input
                type="number"
                placeholder="Max members"
                value={filters.maxMembers}
                onChange={(e) => handleFilterChange('maxMembers', e.target.value)}
                className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
              />
            </div>
          </div>

          {/* Quality Score */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Minimum Quality Score
            </label>
            <input
              type="number"
              step="0.1"
              min="0"
              max="10"
              placeholder="0.0 - 10.0"
              value={filters.qualityScore}
              onChange={(e) => handleFilterChange('qualityScore', e.target.value)}
              className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
            />
            <p className="mt-1 text-xs text-gray-500">Groups with higher quality scores will be prioritized</p>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row gap-3">
        <Button
          variant="primary"
          icon={AdjustmentsHorizontalIcon}
          onClick={handleSaveConfiguration}
          isLoading={saving}
          disabled={isAssignmentProcessActive || (selectedThemes.length === 0 && buildFiltersArray().length === 0)}
        >
          {saving ? 'Saving & Running...' : 
           preview ? `Save Configuration & Run (${preview.totalGroups} groups)` : 
           'Save Configuration & Run'}
        </Button>

        <Button
          variant="secondary"
          icon={PlayIcon}
          onClick={handleRunAutoAssignment}
          isLoading={running}
          disabled={isAssignmentProcessActive}
        >
          {running ? 'Running...' : 'Run with Current Settings'}
        </Button>
      </div>

      {/* Warning for no groups */}
      {preview && preview.totalGroups === 0 && selectedThemes.length > 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-center">
            <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600 mr-2" />
            <div className="text-sm text-yellow-800">
              <p className="font-medium">No groups match the current criteria</p>
              <p>Try adjusting your theme selection or filters to find matching groups.</p>
            </div>
          </div>
        </div>
      )}

      {/* Assignment Results */}
      {assignmentResult && (
        <div className="mt-6 p-4 bg-gray-50 rounded-lg">
          <h4 className="text-sm font-medium text-gray-900 mb-2">Assignment Results</h4>
          <div className="text-sm text-gray-600">
            <p>• Groups processed: {assignmentResult.processed || 0}</p>
            <p>• Groups assigned: {assignmentResult.assigned || 0}</p>
            <p>• Groups skipped: {assignmentResult.skipped || 0}</p>
            {assignmentResult.errors && assignmentResult.errors.length > 0 && (
              <p className="text-red-600">• Errors: {assignmentResult.errors.length}</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectAutoAssignmentEnhanced;
